import { memo, useCallback, useEffect, useState } from "react";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import Snackbar from "@mui/material/Snackbar";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CasinoIcon from "@mui/icons-material/Casino";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import RedoIcon from "@mui/icons-material/Redo";
import RemoveIcon from "@mui/icons-material/Remove";
import UndoIcon from "@mui/icons-material/Undo";
import { copyText, exportImage, exportText } from "../lib/export";
import { toolbarDelay } from "../lib/motion";
import type { PageElement } from "../state/types";

export interface ActionsProps {
  /**
   * Reads the page at the moment an export runs, rather than taking it as a
   * prop. The page is a new array on every erased word, and taking it directly
   * re-rendered this toolbar — a MUI SpeedDial and nine icons — once per mark.
   */
  getPage: () => PageElement[];
  title?: string;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onCycleStyle: () => void;
  onAdjustFontSize: (delta: number) => void;
  onMarkRandom: () => void;
  /** Leaves the erasure; shown as the topmost action of the dial. */
  onCloseErasure: () => void;
}

interface ActionItem {
  icon: React.ReactNode;
  name: string;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * The floating toolbar.
 *
 * Actions that change the document dispatch upward; the two that produce files or
 * clipboard content are handled here, since they are side effects on the browser
 * rather than edits to state.
 */
const Actions = ({
  getPage,
  title,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onCycleStyle,
  onAdjustFontSize,
  onMarkRandom,
  onCloseErasure,
}: ActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleCopy = useCallback(async () => {
    const copied = await copyText(getPage());
    setMessage(copied ? "Poem copied to clipboard" : "Couldn't access the clipboard");
  }, [getPage]);

  const handleExportText = useCallback(async () => {
    try {
      await exportText(getPage(), title);
      setMessage("Text shared");
    } catch {
      setMessage("Couldn't export the text");
    }
  }, [getPage, title]);

  const handleExportImage = useCallback(async () => {
    try {
      await exportImage(title);
      setMessage("Image saved");
    } catch {
      setMessage("Couldn't save the image");
    }
  }, [title]);

  // Keyboard shortcuts, so the app is usable without hunting through the dial.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // The target may be `document` or `window`, which have no `closest`.
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest("input, textarea, [contenteditable='true']")
      ) {
        return;
      }
      if (!(event.metaKey || event.ctrlKey)) return;

      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) onRedo();
        else onUndo();
      } else if (key === "y") {
        event.preventDefault();
        onRedo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onUndo, onRedo]);

  const actions: ActionItem[] = [
    { icon: <UndoIcon />, name: "Undo", onClick: onUndo, disabled: !canUndo },
    { icon: <RedoIcon />, name: "Redo", onClick: onRedo, disabled: !canRedo },
    { icon: <CasinoIcon />, name: "Erase at random", onClick: onMarkRandom },
    { icon: <InvertColorsIcon />, name: "Change style", onClick: onCycleStyle },
    { icon: <AddIcon />, name: "Larger text", onClick: () => onAdjustFontSize(1) },
    { icon: <RemoveIcon />, name: "Smaller text", onClick: () => onAdjustFontSize(-1) },
    { icon: <ContentCopyIcon />, name: "Copy poem", onClick: handleCopy },
    { icon: <ImageIcon />, name: "Save image", onClick: handleExportImage },
    { icon: <DownloadIcon />, name: "Save text", onClick: handleExportText },
    // Last, so it sits at the top of an upward-opening dial.
    { icon: <CloseIcon />, name: "Close erasure", onClick: onCloseErasure },
  ];

  return (
    <>
      <SpeedDial
        ariaLabel="Erasure actions"
        className="page-in"
        style={{ "--delay": toolbarDelay() } as React.CSSProperties}
        sx={{
          position: "fixed",
          bottom: "calc(16px + env(safe-area-inset-bottom))",
          right: "calc(16px + env(safe-area-inset-right))",
          "& .MuiSpeedDial-fab": {
            backgroundColor: "black",
            "&:hover": { backgroundColor: "#3D3C3C" },
          },
        }}
        icon={<SpeedDialIcon />}
        direction="up"
        // The dial is opened and closed by the reader tapping its button, and
        // by nothing else: MUI also opens it on hover and focus and closes it
        // on blur and mouse-leave, which would fold it away mid-use. Escape
        // still closes it, for keyboard readers.
        onOpen={(_event, reason) => {
          if (reason === "toggle") setIsOpen(true);
        }}
        onClose={(_event, reason) => {
          if (reason === "toggle" || reason === "escapeKeyDown") setIsOpen(false);
        }}
        open={isOpen}
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            aria-label={action.name}
            // MUI renders these as buttons; disabling keeps undo/redo honest.
            FabProps={{ disabled: action.disabled }}
            onClick={(event) => {
              event.stopPropagation();
              if (action.disabled) return;
              action.onClick();
            }}
          />
        ))}
      </SpeedDial>

      <Snackbar
        open={message !== null}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message ?? ""}
      />
    </>
  );
};

export default memo(Actions);
